// Best-effort author-name normalization to each citation style's convention.
// Users should review results before publishing.

function parseName(raw) {
  const name = (raw || "").trim().replace(/\.$/, "");
  if (!name) return null;
  let surname = "";
  let givenTokens = [];
  if (name.includes(",")) {
    const [sur, giv] = name.split(",");
    surname = sur.trim();
    givenTokens = (giv || "").trim().split(/[\s.]+/).filter(Boolean);
  } else {
    const toks = name.split(/\s+/);
    const last = toks[toks.length - 1];
    // "Smith AB" style: trailing all-caps initials block
    if (toks.length > 1 && /^[A-Z]{1,4}$/.test(last)) {
      surname = toks.slice(0, -1).join(" ");
      givenTokens = last.split("");
    } else {
      surname = last;
      givenTokens = toks.slice(0, -1);
    }
  }
  const initials = givenTokens
    .map((t) => (t.match(/[A-Za-z]/) ? t[0].toUpperCase() : ""))
    .filter(Boolean);
  return { surname: capSurname(surname.trim()), initials };
}

// Capitalize the first letter of each surname part, preserving any internal caps (McGraw, DeVito).
function capSurname(s) {
  return (s || "").replace(/[A-Za-z][^\s-]*/g, (w) => w.charAt(0).toUpperCase() + w.slice(1));
}

function splitAuthors(str) {
  // Split on ';' / newlines first, then split each segment on ' and ' / '&' ONLY when the
  // segment is a personal name — so organization names like "Centers for Disease Control and
  // Prevention" are never broken apart.
  const segments = (str || "").split(/\s*;\s*|\n/).map((s) => s.trim()).filter(Boolean);
  const out = [];
  for (const seg of segments) {
    // Keep a segment intact when it's an organization: an org keyword, or a lowercase connector
    // ("for"/"of"/"the") that appears in org names ("Centers for Disease Control and Prevention")
    // but not in personal pairs ("Anna Smith and Rob Doe").
    if (hasOrgWord(seg) || /\b(for|of|the)\b/i.test(seg)) { out.push(seg); continue; }
    seg.split(/\s+and\s+|\s*&\s*/i).map((s) => s.trim()).filter(Boolean).forEach((s) => out.push(s));
  }
  return out;
}

// Detect organizational / group authors that must NOT be reduced to initials.
// Keyword part is case-insensitive; the acronym part is case-sensitive with Unicode-aware
// boundaries so it never matches inside a diacritic name (e.g. "UN" in "Unützer").
const ORG_KEYWORDS = /\b(univers\w*|institut\w*|organi[sz]ation|assoc\w*|department|dept|ministry|centre|center|society|committee|foundation|group|administration|bureau|council|corp\w*|company|inc|ltd|llc|agency|office|network|consortium|collaboration|programme|program|hospital|clinic|school|college|laborator\w*|labs?|task force|services)\b/i;
const ORG_ACRONYMS = /(?<!\p{L})(WHO|CDC|NASA|UNESCO|UNICEF|OECD|NIH|NIMH|NIDA|NIAAA|SAMHSA|USPSTF|AHRQ|HHS|CMS|ACOG|AAP|FDA|EPA|UN)(?!\p{L})/u;
function hasOrgWord(n) {
  return ORG_KEYWORDS.test(n) || ORG_ACRONYMS.test(n);
}
// All-caps acronym group authors like SAMHSA, WHO, NIMH, USPSTF (2–11 uppercase letters).
export function isAcronymOrg(name) {
  const n = (name || "").trim().replace(/\.$/, "");
  return /^[A-Z][A-Z&.\-]{1,10}$/.test(n) && /[A-Z]{2,}/.test(n) && !/[a-z]/.test(n);
}
export function looksLikeOrg(name) {
  const n = (name || "").trim();
  if (!n) return false;
  if (hasOrgWord(n) || isAcronymOrg(n)) return true;
  if (!n.includes(",")) {
    const toks = n.split(/\s+/);
    // 4+ words with no initial-looking token -> likely an organization name
    if (toks.length >= 4 && !toks.some((t) => /^[A-Z]\.?$/.test(t))) return true;
  }
  return false;
}
// Stricter org test for author-vs-title decisions: an org keyword, OR a Title-Case multi-word
// name (>=2 capitalized words after the first, no initials) like "Centers for Disease Control
// and Prevention" — but NOT a sentence-case page title like "Understanding anxiety disorders".
export function looksLikeOrgName(name) {
  const n = (name || "").trim().replace(/\.$/, "");
  if (!n) return false;
  if (hasOrgWord(n) || isAcronymOrg(n)) return true;
  if (n.includes(",")) return false;
  const toks = n.split(/\s+/);
  if (toks.length < 2 || toks.some((t) => /^[A-Z]\.?$/.test(t))) return false;
  const caps = toks.slice(1).filter((t) => /^[A-Z]/.test(t)).length;
  return caps >= 2;
}
function keepOrg(s) {
  return s.trim().replace(/\.$/, "");
}

function fmtName(parsed, style) {
  const { surname, initials } = parsed;
  const dotted = initials.map((i) => `${i}.`).join(" "); // "F. M."
  const tight = initials.join(""); // "FM"
  if (!surname) return "";
  switch (style) {
    case "APA":
      return dotted ? `${surname}, ${dotted}` : surname;
    case "Vancouver":
      return tight ? `${surname} ${tight}` : surname;
    case "IEEE":
      return dotted ? `${dotted} ${surname}` : surname;
    case "MLA":
    case "Chicago":
    default:
      return dotted ? `${surname}, ${dotted}` : surname; // handled positionally below
  }
}

export function normalizeAuthors(authorsStr, style) {
  const items = splitAuthors(authorsStr);
  if (!items.length) return authorsStr || "";
  const isOrg = items.map(looksLikeOrg);
  const parsed = items.map((s, i) => (isOrg[i] ? null : parseName(s)));

  if (style === "APA") {
    const names = items.map((s, i) => (isOrg[i] ? keepOrg(s) : (parsed[i] ? fmtName(parsed[i], "APA") : s)));
    if (names.length === 1) return names[0];
    // APA 7: 21+ authors -> first 19, ellipsis, then the final author (no ampersand).
    if (names.length > 20) {
      return names.slice(0, 19).join(", ") + ", … " + names[names.length - 1];
    }
    return names.slice(0, -1).join(", ") + ", & " + names[names.length - 1];
  }
  if (style === "Vancouver") {
    return items.map((s, i) => (isOrg[i] ? keepOrg(s) : (parsed[i] ? fmtName(parsed[i], "Vancouver") : s))).join(", ");
  }
  if (style === "IEEE") {
    const names = items.map((s, i) => (isOrg[i] ? keepOrg(s) : (parsed[i] ? fmtName(parsed[i], "IEEE") : s)));
    if (names.length === 1) return names[0];
    return names.slice(0, -1).join(", ") + " and " + names[names.length - 1];
  }
  // MLA / Chicago: first author inverted "Surname, F. M."; the rest "F. M. Surname"
  const first = isOrg[0] ? keepOrg(items[0]) : (parsed[0] ? fmtName(parsed[0], "APA") : items[0]);
  if (items.length === 1) return first;
  const rest = items.slice(1).map((s, i) => {
    const idx = i + 1;
    if (isOrg[idx]) return keepOrg(s);
    const p = parsed[idx];
    if (!p) return s;
    const dotted = p.initials.map((x) => `${x}.`).join(" ");
    return dotted ? `${dotted} ${p.surname}` : p.surname;
  });
  if (rest.length === 1) return `${first}, and ${rest[0]}`;
  return `${first}, ${rest.slice(0, -1).join(", ")}, and ${rest[rest.length - 1]}`;
}
